import { formatMoney } from '@app/shared'
import { useState } from 'react'
import { Button } from '../ui'
import { PledgeForm } from './PledgeForm'
import './donate.css'

/**
 * What a gift above the ceiling is offered instead of a refusal.
 *
 * The ceiling exists so a mistyped amount is a refused payment rather than a
 * refund the office chases — but a figure this size is usually not a typo, it
 * is a commitment. The shul takes one as a PLEDGE paid in installments, so the
 * page says so and opens the form rather than stopping at "no".
 *
 * **This is the seam, and it is one component on purpose.** Everything the
 * pledge form needs is already a prop here — the figure, what the donor said it
 * was for, and whatever they have already typed about themselves — so neither
 * donate page has to know what is behind it.
 */
export function SplitIntoPlan({
  orgId,
  campaignId,
  amountCents,
  forLabel,
  donorName,
  donorEmail,
  describedId,
  onCheckoutComplete,
}: {
  orgId: string
  /** The campaign year they were on, where they were on one. */
  campaignId?: string
  /** What the donor typed, in integer cents. Above the gift ceiling. */
  amountCents: number
  /** What they said it was for, where the flow had reached one. */
  forLabel: string | null
  /** What they have already said about themselves, so the pledge form does not
   *  ask for it a second time. */
  donorName?: string
  donorEmail?: string
  /** The id the amount field points at, so the message names the control. */
  describedId: string
  onCheckoutComplete: (checkoutId: string) => void
}) {
  const [open, setOpen] = useState(false)

  /* The id names the SENTENCE, which is what the amount field is described by.
     A description is flattened to one string, so an id on a container holding
     the form would read every label in it out as the field's description. The
     sentence stands whether or not the form is open, so the reference never
     dangles. */
  return (
    <div className="donate__plan">
      {/* The sentence is the alert, not the region around it: opening the form
          inside a live region would read the whole form out. */}
      <p className="donate__plan-says" id={describedId} role="alert">
        {formatMoney(amountCents)} is more than this page takes on a card. A
        gift that size is pledged and paid in installments.
      </p>
      {open ? (
        <PledgeForm
          orgId={orgId}
          campaignId={campaignId}
          amountCents={amountCents}
          forLabel={forLabel ?? 'A pledge'}
          donorName={donorName}
          donorEmail={donorEmail}
          onCancel={() => setOpen(false)}
          onCheckoutComplete={onCheckoutComplete}
        />
      ) : (
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Split it into a plan
        </Button>
      )}
    </div>
  )
}
